import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import { supabase } from '../lib/supabase';

const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const handleEmailVerification = async () => {
      try {
        // Get all URL parameters
        const token = searchParams.get('token');
        const type = searchParams.get('type');
        const redirectTo = searchParams.get('redirect_to');
        const accessToken = searchParams.get('access_token');
        const refreshToken = searchParams.get('refresh_token');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        console.log('=== Email Verification Debug ===');
        console.log('URL:', window.location.href);
        console.log('All search params:', Object.fromEntries(searchParams.entries()));
        console.log('Verification params:', { 
          token: token ? `${token.substring(0, 10)}...` : 'missing', 
          type, 
          redirectTo, 
          accessToken: accessToken ? 'present' : 'missing',
          refreshToken: refreshToken ? 'present' : 'missing',
          error,
          errorDescription
        });

        // Check for error parameters first
        if (error) {
          console.error('Verification error from Supabase:', error, errorDescription);
          setVerificationStatus('error');
          setErrorMessage(errorDescription || 'Verification failed. Please try again.');
          return;
        }

        // Scenario 1: Supabase automatically verified the user and provided tokens
        if (accessToken && refreshToken) {
          console.log('User already verified by Supabase, setting session');
          
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) {
            console.error('Session error:', error);
            setVerificationStatus('error');
            setErrorMessage('Failed to complete verification. Please try again.');
            return;
          }

          console.log('Session set successfully:', data.user?.email_confirmed_at);
          if (data.user && data.user.email_confirmed_at) {
            setVerificationStatus('success');
            // Redirect to verification success page after a short delay
            setTimeout(() => {
              navigate('/auth/verification-success');
            }, 2000);
            return;
          }
        }

        // Scenario 2: Manual verification with token only
        if (token && type === 'signup') {
          console.log('Attempting manual verification with token');
          
          try {
            // First, try to get the current user to see if they're already verified
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            
            if (currentUser && currentUser.email_confirmed_at) {
              console.log('User is already verified');
              setVerificationStatus('success');
              // Redirect to verification success page after a short delay
              setTimeout(() => {
                navigate('/auth/verification-success');
              }, 2000);
              return;
            }

            // If not verified, try manual verification
            // Note: This approach might not work with the current Supabase setup
            // as it requires the email address which we don't have
            console.log('Manual verification not supported without email address');
            setVerificationStatus('error');
            setErrorMessage('Verification link is invalid or expired. Please try signing up again.');
            return;
            
          } catch (verifyError) {
            console.error('Verification attempt failed:', verifyError);
            setVerificationStatus('error');
            setErrorMessage('Failed to verify email. Please try again.');
            return;
          }
        }

        // Scenario 3: Check if user is already verified (fallback)
        console.log('Checking current user status');
        const { data: { user }, error: userError } = await supabase.auth.getUser();

        if (userError) {
          console.error('User check error:', userError);
        }

        console.log('Current user:', user?.email, 'Confirmed at:', user?.email_confirmed_at);
        if (user && user.email_confirmed_at) {
          console.log('User is already verified');
          setVerificationStatus('success');
          // Redirect to verification success page after a short delay
          setTimeout(() => {
            navigate('/auth/verification-success');
          }, 2000);
          return;
        }

        // If we reach here, verification failed
        console.log('Verification failed - no valid verification found');
        setVerificationStatus('error');
        setErrorMessage('Email verification failed. Please try again or contact support.');

      } catch (err) {
        console.error('Unexpected error during verification:', err);
        setVerificationStatus('error');
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
    };

    handleEmailVerification();
  }, [searchParams, navigate]);

  const getStatusContent = () => {
    switch (verificationStatus) {
      case 'loading':
        return {
          icon: (
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#177508"/>
              <path d="M32 8C18.745 8 8 18.745 8 32s10.745 24 24 24 24-10.745 24-24S45.255 8 32 8zm0 44c-11.046 0-20-8.954-20-20s8.954-20 20-20 20 8.954 20 20-8.954 20-20 20z" fill="white"/>
              <path d="M32 16c-8.837 0-16 7.163-16 16s7.163 16 16 16 16-7.163 16-16-7.163-16-16-16zm0 28c-6.627 0-12-5.373-12-12s5.373-12 12-12 12 5.373 12 12-5.373 12-12 12z" fill="white"/>
            </svg>
          ),
          title: 'Verifying your email...',
          message: 'Please wait while we confirm your email address.',
          color: '#177508'
        };
      
      case 'success':
        return {
          icon: (
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#177508"/>
              <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ),
          title: 'Email Verified Successfully!',
          message: 'Your email has been confirmed. You will be redirected shortly.',
          color: '#177508'
        };
      
      case 'error':
        return {
          icon: (
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#C9121A"/>
              <path d="M20 20L44 44M44 20L20 44" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ),
          title: 'Verification Failed',
          message: errorMessage,
          color: '#C9121A'
        };
    }
  };

  const content = getStatusContent();

  return (
    <div className='verification-container'>
      <div className='verification-content'>
        <div className='verification-card'>
          <div className='verification-icon'>
            {content.icon}
          </div>
          
          <h1 className='titles large color black'>{content.title}</h1>
          
          <p className='paragraphs medium color charcoal'>
            {content.message}
          </p>
          
          {verificationStatus === 'error' && (
            <div className='verification-actions'>
              <button 
                className='btn primary'
                onClick={() => window.location.reload()}
              >
                Try Again
              </button>
              <button 
                className='btn secondary'
                onClick={() => navigate('/')}
              >
                Go to Home
              </button>
            </div>
          )}
          
          {verificationStatus === 'loading' && (
            <div className='loading-spinner'>
              <div className='spinner'></div>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default EmailVerification; 